// The LIB memberships console route subtree. Code-based routes per
// DEV-GUIDE-002 §4 — a package-composed subtree hand-writes `.lazy()` +
// `createLazyRoute`. This factory hangs `/library/memberships` off the portal's
// root route so router.tsx stays the single wiring point.
//
// GATING: the console opens at lib:patrons.read — the DESK must see what a
// member owes in order to collect it (dues are receivables). The narrower tiers
// are enforced per screen and by the API: plan WRITES need lib:admin (they set
// fees and entitlements), and the receipts ledger + revenue need lib:reports
// (they are financial reporting — an assistant who could read receipts could
// reconstruct the revenue that tier denies them).
import { Outlet, createRoute } from "@tanstack/solid-router";
import { lazyRoute } from "@erp/ui/route-kit";
import { ConsoleLayout } from "@erp/ui/console-layout";
import { panelSearch, type PanelSearch } from "@erp/ui/side-panel";
import type { AnyRoute } from "@tanstack/solid-router";

/* ───────────────────────────── URL-as-state (side panels) ────────────────────
 * The plan editor and the collect-dues sheet are `@erp/ui/side-panel` SidePanels,
 * and a panel is a VIEW — so `?panel=…&panelId=…&panelWide=1` lives in the route
 * search and Back closes the panel, a pasted link opens straight into the record.
 * Each route declares its OWN closed set, so `?panel=collect` on the plan
 * registry (or `?panel=edit` on dues) heals to "no panel" rather than opening
 * something that route cannot render. Money never opens from an unhealed param. */
export const PLAN_PANELS = ["new", "edit"] as const;
export const DUES_PANELS = ["collect"] as const;

export type PlanPanel = (typeof PLAN_PANELS)[number];
export type DuesPanel = (typeof DUES_PANELS)[number];

export function validatePlansSearch(input: Record<string, unknown>): PanelSearch {
  return panelSearch(PLAN_PANELS, input);
}
export function validateDuesSearch(input: Record<string, unknown>): PanelSearch {
  return panelSearch(DUES_PANELS, input);
}

function MembershipsLayout() {
  return (
    <ConsoleLayout
      perm="lib:patrons.read"
      deniedMessage="This account cannot open memberships. Ask an administrator for patron access."
      skeletonId="memberships-skeleton"
      deniedId="memberships-denied"
      unreachableId="memberships-unreachable"
    >
      <Outlet />
    </ConsoleLayout>
  );
}

export function makeLibMembershipsRoutes<TParent extends AnyRoute>(rootRoute: TParent) {
  const membershipsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "library/memberships",
    component: MembershipsLayout,
  });

  const plansRoute = createRoute({
    getParentRoute: () => membershipsRoute,
    path: "plans",
    validateSearch: validatePlansSearch,
  }).lazy(lazyRoute(() => import("../../screens/admin/memberships/MembershipPlans.js")));

  const duesRoute = createRoute({
    getParentRoute: () => membershipsRoute,
    path: "dues",
    validateSearch: validateDuesSearch,
  }).lazy(lazyRoute(() => import("../../screens/admin/memberships/MemberDues.js")));

  const paymentsRoute = createRoute({
    getParentRoute: () => membershipsRoute,
    path: "payments",
  }).lazy(lazyRoute(() => import("../../screens/admin/memberships/MemberPayments.js")));

  const revenueRoute = createRoute({
    getParentRoute: () => membershipsRoute,
    path: "revenue",
  }).lazy(lazyRoute(() => import("../../screens/admin/memberships/RevenueOverview.js")));

  // The plan registry is the subtree INDEX so `/library/memberships` lands
  // somewhere useful rather than on an empty layout.
  const indexRoute = createRoute({
    getParentRoute: () => membershipsRoute,
    path: "/",
    validateSearch: validatePlansSearch,
  }).lazy(lazyRoute(() => import("../../screens/admin/memberships/MembershipPlans.js"), (m) => m.IndexRoute));

  return [membershipsRoute.addChildren([indexRoute, plansRoute, duesRoute, paymentsRoute, revenueRoute])];
}
